import React, { useState } from 'react';
import { Package, Search, Mail, Phone, CheckCircle, FileText, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';

const PiecesDetacheesQuai = () => {
  const [selectedFamily, setSelectedFamily] = useState('all');

  const pieceFamilies = [
    {
      id: 'hydraulique',
      title: 'Hydraulique',
      icon: '🔧',
      items: [
        'Vérins de levage',
        'Vérins de lèvre',
        'Flexibles hydrauliques',
        'Raccords et adaptateurs',
        'Joints toriques et garnitures',
        'Pompes et centrales hydrauliques',
        'Huiles hydrauliques adaptées',
        'Consommables hydrauliques'
      ]
    },
    {
      id: 'mecanique',
      title: 'Mécanique',
      icon: '⚙️',
      items: [
        'Lèvres de niveleurs',
        'Axes et pivots',
        'Charnières renforcées',
        'Bagues et paliers',
        'Articulations',
        'Composants de fixation',
        'Éléments de structure',
        'Systèmes de verrouillage'
      ]
    },
    {
      id: 'portes',
      title: 'Fermetures Industrielles',
      icon: '🚪',
      items: [
        'Galets de portes sectionnelles',
        'Câbles et ressorts',
        'Joints d\'étanchéité',
        'Panneaux de remplacement',
        'Motorisations',
        'Commandes et télécommandes',
        'Lames de rideaux métalliques',
        'Coulisses et guides'
      ]
    }
  ];

  const filteredFamilies = selectedFamily === 'all' 
    ? pieceFamilies 
    : pieceFamilies.filter(f => f.id === selectedFamily);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <SEO 
        title="Pièces Détachées Niveleurs & Portes Industrielles | ORION INDUSTRIE"
        description="Catalogue de pièces détachées pour niveleurs de quai, portes sectionnelles et rideaux métalliques : vérins, flexibles, lèvres, joints, motorisations. Livraison et pose sur site."
        keywords="pièces détachées niveleur quai, vérin niveleur, lèvre niveleur, flexible hydraulique, joint niveleur, pièces porte sectionnelle, motorisation porte industrielle"
      />
      <Header />

      {/* Hero */}
      <section className="bg-gradient-to-r from-[#020d2a] to-slate-800 text-white py-20 mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-6">
            <Package className="w-12 h-12 text-[#FF6600]" />
            <h1 className="text-4xl sm:text-5xl font-bold">
              Pièces Détachées pour Équipements de Quai
            </h1>
          </div>
          <p className="text-xl text-slate-300 max-w-4xl mb-8">
            Pièces de rechange pour niveleurs de quai, portes sectionnelles et rideaux métalliques. 
            Demande de disponibilité, devis et remplacement sur site.
          </p>
          <div className="flex flex-wrap gap-4">
            <Button 
              onClick={() => document.getElementById('formulaire').scrollIntoView({ behavior: 'smooth' })}
              size="lg"
              className="bg-[#FF6600] hover:bg-[#E65A00] text-white"
            >
              Demander une pièce
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <a href="tel:+33659452562">
              <Button 
                size="lg"
                variant="outline"
                className="border-2 border-gray-300 text-white hover:bg-white/10"
              >
                <Phone className="mr-2 w-5 h-5" />
                06 59 45 25 62
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Filtres */}
      <section className="py-12 bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-4 justify-center">
            <button
              onClick={() => setSelectedFamily('all')}
              className={`px-6 py-3 rounded-lg font-semibold transition-colors ${
                selectedFamily === 'all'
                  ? 'bg-[#FF6600] text-white'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              Toutes les familles
            </button>
            {pieceFamilies.map(family => (
              <button
                key={family.id}
                onClick={() => setSelectedFamily(family.id)}
                className={`px-6 py-3 rounded-lg font-semibold transition-colors ${
                  selectedFamily === family.id
                    ? 'bg-[#FF6600] text-white'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                {family.icon} {family.title}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Catalogue */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-8">
            {filteredFamilies.map(family => (
              <div key={family.id} className="bg-white rounded-2xl p-8 shadow-lg">
                <div className="text-center mb-6">
                  <div className="text-5xl mb-4">{family.icon}</div>
                  <h2 className="text-2xl font-bold text-slate-900">{family.title}</h2>
                </div>
                <ul className="space-y-3">
                  {family.items.map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Comment commander */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-slate-900 text-center mb-12">
            Comment commander une pièce détachée ?
          </h2>

          <div className="grid md:grid-cols-4 gap-6 mb-12">
            {[
              { step: '1', title: 'Identifier', desc: 'Marque, modèle et référence de votre équipement' },
              { step: '2', title: 'Photographier', desc: 'Plaque constructeur et pièce défectueuse' },
              { step: '3', title: 'Demander', desc: 'Disponibilité et tarif via notre formulaire' },
              { step: '4', title: 'Recevoir', desc: 'Livraison ou remplacement sur site' }
            ].map(({ step, title, desc }) => (
              <div key={step} className="text-center">
                <div className="w-16 h-16 bg-[#FF6600] text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                  {step}
                </div>
                <h3 className="font-bold text-slate-900 mb-2">{title}</h3>
                <p className="text-sm text-slate-600">{desc}</p>
              </div>
            ))}
          </div>

          <div className="bg-orange-50 border-l-4 border-[#FF6600] p-6 rounded-lg max-w-4xl mx-auto">
            <h3 className="font-bold text-slate-900 mb-2">Disponibilité des pièces</h3>
            <p className="text-slate-700">
              La disponibilité des pièces détachées dépend de la marque, du modèle et des stocks fabricants. 
              Nous vous confirmons la disponibilité et le délai après étude de votre demande.
            </p>
          </div>
        </div>
      </section>

      {/* Formulaire */}
      <section id="formulaire" className="py-20 bg-slate-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl p-8 lg:p-12 shadow-xl">
            <div className="text-center mb-8">
              <FileText className="w-12 h-12 text-[#FF6600] mx-auto mb-4" />
              <h2 className="text-3xl font-bold text-slate-900 mb-4">
                Demande de Pièce Détachée
              </h2>
              <p className="text-slate-600">
                Remplissez ce formulaire pour obtenir une disponibilité et un tarif
              </p>
            </div>

            <form className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-slate-900 mb-2">
                    Société *
                  </label>
                  <input
                    type="text"
                    required
                    className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-[#FF6600] focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-900 mb-2">
                    Contact *
                  </label>
                  <input
                    type="text"
                    required
                    className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-[#FF6600] focus:border-transparent"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-slate-900 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    required
                    className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-[#FF6600] focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-900 mb-2">
                    Téléphone *
                  </label>
                  <input
                    type="tel"
                    required
                    className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-[#FF6600] focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-900 mb-2">
                  Type d'équipement *
                </label>
                <select
                  required
                  className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-[#FF6600] focus:border-transparent"
                >
                  <option value="">Sélectionnez...</option>
                  <option value="niveleur">Niveleur de quai</option>
                  <option value="porte-sectionnelle">Porte sectionnelle</option>
                  <option value="rideau-metallique">Rideau métallique</option>
                  <option value="autre">Autre équipement</option>
                </select>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-slate-900 mb-2">
                    Marque (si connue)
                  </label>
                  <input
                    type="text"
                    className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-[#FF6600] focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-900 mb-2">
                    Modèle (si connu)
                  </label>
                  <input
                    type="text"
                    className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-[#FF6600] focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-900 mb-2">
                  Pièce recherchée *
                </label>
                <textarea
                  required
                  rows={4}
                  placeholder="Décrivez la pièce recherchée (ex: vérin de levage, lèvre de niveleur, flexible hydraulique...)"
                  className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-[#FF6600] focus:border-transparent"
                ></textarea>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-900 mb-2">
                  Besoin de pose sur site ?
                </label>
                <div className="flex gap-6">
                  <label className="flex items-center gap-2">
                    <input type="radio" name="pose" value="oui" className="w-4 h-4 text-[#FF6600]" />
                    <span className="text-slate-700">Oui, remplacement sur site</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="radio" name="pose" value="non" className="w-4 h-4 text-[#FF6600]" />
                    <span className="text-slate-700">Non, livraison uniquement</span>
                  </label>
                </div>
              </div>

              <div className="bg-slate-50 rounded-lg p-6">
                <p className="text-sm text-slate-600 mb-3">
                  <strong>Conseil :</strong> Pour accélérer le traitement, joignez si possible :
                </p>
                <ul className="text-sm text-slate-600 space-y-1">
                  <li>• Photo de la plaque constructeur</li>
                  <li>• Photo de la pièce défectueuse</li>
                  <li>• Numéro de série de l'équipement</li>
                </ul>
              </div>

              <Button
                type="submit"
                size="lg"
                className="w-full bg-[#FF6600] hover:bg-[#E65A00] text-white"
              >
                Envoyer ma demande
                <Mail className="ml-2 w-5 h-5" />
              </Button>

              <p className="text-xs text-slate-500 text-center">
                Nous vous répondons sous 24-48h avec la disponibilité et le tarif de la pièce.
              </p>
            </form>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-[#020d2a] to-slate-800 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-4">
            Une question sur une pièce détachée ?
          </h2>
          <p className="text-xl text-slate-300 mb-8">
            Notre équipe vous conseille et vous aide à identifier la pièce adaptée
          </p>
          <a href="tel:+33659452562">
            <Button 
              size="lg"
              className="bg-[#FF6600] hover:bg-[#E65A00] text-white"
            >
              <Phone className="mr-2 w-5 h-5" />
              Contactez-nous : 06 59 45 25 62
            </Button>
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PiecesDetacheesQuai;